import { QuoteFeedbackModal } from "@/components/modal/quote-feedback";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuoteContext } from "@/contexts/quote-context";
import { cn } from "@/lib/utils";
import { jsonMotivo } from "@/services/flowQuote";
import { Option } from "@/types/Common";
import { MotivoCompra } from "@/types/FlowQuote";
import { normalizeEmail, suggestEmailCorrection } from "@/utils/emailSuggest";
import { formatPhone } from "@/utils/format";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Loader2, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Stepper } from "../../ui/stepper";

import {
  trackCotacaoDadosPessoaisCaptchaErro,
  trackCotacaoDadosPessoaisSubmitError,
  trackCotacaoDadosPessoaisSubmitSuccess,
  trackCotacaoVoltarParaVeiculo,
} from "@/analytics/tracking";

const personalSchema = z.object({
  name: z
    .string({ message: "Informe o nome" })
    .min(1, { message: "Informe seu nome" }),
  ddi: z.string().min(1, "Selecione o DDI"),
  phone: z
    .string({ message: "Informe o número" })
    .min(1, { message: "Informe seu telefone" }),
  email: z
    .string({ message: "Informe o email" })
    .email({ message: "Informe um email válido" }),
  reason: z.string().optional(),
  transportSchedule: z.string({
    message: "Selecione uma previsão de transporte",
  }),
});

type PersonalFormValues = z.infer<typeof personalSchema>;

interface PersonalInfoStepProps {
  onNext: (quoteId: string, token: string) => void;
  onBack: () => void;
}

export function StepTwo({ onNext, onBack }: PersonalInfoStepProps) {
  const { personalData, sendQuote } = useQuoteContext();
  const [isLoading, setIsLoading] = useState(false);
  const [reasonsOptions, setReasonsOptions] = useState<Option[]>([]);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState<
    "success" | "warning" | "error" | "default"
  >("default");

  const form = useForm<PersonalFormValues>({
    resolver: zodResolver(personalSchema),
    defaultValues: {
      name: personalData?.name ?? "",
      ddi: "BR",
      phone: personalData?.phone ?? "",
      email: personalData?.email ?? "",
      reason: personalData?.reason ?? "",
      transportSchedule: personalData?.transportSchedule ?? "",
    },
  });

  const validateReason = async (
    input?: string | null,
  ): Promise<string | null> => {
    if (!input) return "";
    const exists = reasonsOptions.some((v) => v.value === input);
    if (!exists) {
      form.setError("reason", {
        type: "manual",
        message: "Selecione um motivo da lista",
      });
      return null;
    }
    return input;
  };

  const onSubmit = async (data: PersonalFormValues) => {
    if (!captchaToken) {
      trackCotacaoDadosPessoaisCaptchaErro({
        name: data.name,
        ddi: data.ddi,
        phone: data.phone,
        email: data.email,
        reason: data.reason,
        transportSchedule: data.transportSchedule,
      });

      setModalMessage("Por favor, confirme que você não é um robô.");
      setModalType("warning");
      setModalOpen(true);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const isValidReason = await validateReason(data.reason ?? "");
    if (isValidReason === null) {
      setIsLoading(false);
      return;
    }

    try {
      const success = await sendQuote(data);
      if (success?.cve_codigo) {
        trackCotacaoDadosPessoaisSubmitSuccess({
          name: data.name,
          ddi: data.ddi,
          phone: data.phone,
          email: data.email,
          reason: isValidReason || "",
          transportSchedule: data.transportSchedule,
          quote_id: success.cve_codigo,
        });
        onNext(success.cve_codigo, success.cve_token);
      } else {
        trackCotacaoDadosPessoaisSubmitError(
          {
            name: data.name,
            ddi: data.ddi,
            phone: data.phone,
            email: data.email,
            reason: isValidReason || "",
            transportSchedule: data.transportSchedule,
          },
          { message: "Resposta sem cve_codigo" },
        );
        setModalMessage("Não foi possível gerar a cotação no momento.");
        setModalType("error");
        setModalOpen(true);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : undefined;
      const errorCode =
        typeof err === "object" && err !== null && "code" in err
          ? String((err as { code?: unknown }).code)
          : undefined;
      trackCotacaoDadosPessoaisSubmitError(
        {
          name: data.name,
          ddi: data.ddi,
          phone: data.phone,
          email: data.email,
          reason: isValidReason || "",
          transportSchedule: data.transportSchedule,
        },
        { message: errorMessage, code: errorCode },
      );

      setModalMessage(
        "Erro ao gerar cotação: " + (errorMessage || "tente novamente"),
      );
      setModalType("error");
      setModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const onBackClick = () => {
    trackCotacaoVoltarParaVeiculo();
    onBack();
  };

  const today = new Date();
  const transportDateOptions = [
    {
      label: "O mais breve possível",
      value: format(
        new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000),
        "yyyy-MM-dd",
      ),
    },
    {
      label: "Nos próximos dias",
      value: format(
        new Date(today.getTime() + 10 * 24 * 60 * 60 * 1000),
        "yyyy-MM-dd",
      ),
    },
    {
      label: "Em até 30 dias",
      value: format(
        new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000),
        "yyyy-MM-dd",
      ),
    },
    {
      label: "Acima de 30 dias",
      value: format(
        new Date(today.getTime() + 40 * 24 * 60 * 60 * 1000),
        "yyyy-MM-dd",
      ),
    },
  ];

  useEffect(() => {
    async function fetchMotivos() {
      try {
        const data = await jsonMotivo();
        setReasonsOptions(
          data.map((reason: MotivoCompra) => ({
            label: `${reason.cmc_descricao} `,
            value: `${reason.cmc_codigo}`,
          })),
        );
      } catch (err) {
        console.error("Erro ao buscar motivos:", err);
      }
    }
    fetchMotivos();
  }, []);

  return (
    <div className="flex flex-col h-full">
      <div className="p-6 md:p-8 flex flex-col gap-6 md:gap-8 flex-1 overflow-y-auto pb-12 md:pb-8">
        <div>
          <h1 className="hidden md:block text-2xl md:text-3xl font-semibold text-n-primary text-center">
            Detalhes finais para calcular seu valor:
          </h1>
          <div className="md:hidden flex flex-col items-center justify-start">
            <h1 className="text-lg font-bold text-n-text-neutral text-center">
              Calcule seu transporte agora
            </h1>
            <h2 className="text-lg text-n-text">
              ou ligue para{" "}
              <a
                href="tel:08008780708"
                className="font-bold underline underline-offset-2 hover:text-n-primary text-n-text-green transition-colors cursor-pointer"
              >
                0800 878 0708
              </a>
            </h2>
          </div>
        </div>

        <Stepper currentStep={2} totalSteps={2} />

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col md:gap-4 gap-2"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input placeholder="Nome completo" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-2">
              <FormField
                control={form.control}
                name="ddi"
                render={({ field }) => (
                  <FormItem className="w-[80px]">
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BR">BR</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input
                        placeholder="Whatsapp: (00) 00000-0000"
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(formatPhone(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="Email para receber a cotação"
                      {...field}
                      onBlur={(e) => {
                        field.onBlur();

                        const raw = e.target.value;
                        const { email } = normalizeEmail(raw);
                        const s = suggestEmailCorrection(email);
                        setEmailSuggestion(s);

                        if (s) {
                          form.setError("email", {
                            type: "validate",
                            message: `Verifique o domínio. Você quis dizer ${s}?`,
                          });
                        } else {
                          form.clearErrors("email");
                        }
                      }}
                      onChange={(e) => {
                        field.onChange(e);
                        if (emailSuggestion) setEmailSuggestion(null);
                      }}
                    />
                  </FormControl>

                  {emailSuggestion && (
                    <div className="text-sm mt-1">
                      <span className="text-n-red">Você quis dizer </span>
                      <button
                        type="button"
                        className="underline"
                        onClick={() => {
                          form.setValue("email", emailSuggestion, {
                            shouldValidate: true,
                          });
                          setEmailSuggestion(null);
                          form.clearErrors("email");
                        }}
                      >
                        {emailSuggestion}
                      </button>
                      <span className="text-n-red">?</span>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <div className="space-y-1">
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger
                      className={cn(
                        "w-full",
                        !field.value && "text-muted-foreground",
                      )}
                    >
                      <SelectValue placeholder="Motivo do Transporte" />
                    </SelectTrigger>
                    <SelectContent>
                      {reasonsOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.formState.errors.reason && (
                    <p className="text-n-error text-sm">
                      {form.formState.errors.reason.message as string}
                    </p>
                  )}
                </div>
              )}
            />

            <FormField
              control={form.control}
              name="transportSchedule"
              render={({ field }) => (
                <div className="space-y-1">
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger
                      className={cn(
                        "w-full",
                        !field.value && "text-muted-foreground",
                      )}
                    >
                      <SelectValue placeholder="Quando pretende realizar este transporte" />
                    </SelectTrigger>
                    <SelectContent>
                      {transportDateOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.formState.errors.transportSchedule && (
                    <p className="text-n-error text-sm">
                      {
                        form.formState.errors.transportSchedule
                          .message as string
                      }
                    </p>
                  )}
                </div>
              )}
            />

            <AnimatePresence>
              {!captchaToken && (
                <motion.div
                  className="flex flex-1 items-start"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex flex-col gap-2">
                    <ReCAPTCHA
                      sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                      onChange={(token) => setCaptchaToken(token)}
                      onExpired={() => setCaptchaToken(null)}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </Form>
      </div>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex gap-2 z-50">
        <Button
          type="button"
          variant="green"
          className="flex-1 flex items-center justify-center gap-2 bg-n-beige"
          onClick={() => window.open("tel:08008780708", "_self")}
        >
          <Phone className="w-4 h-4" />
          Ligue Agora
        </Button>
        <Button
          type="button"
          className="flex-1 flex items-center justify-center gap-2"
          disabled={isLoading}
          onClick={form.handleSubmit(onSubmit)}
          id="btn-cotacao-step-final"
        >
          {isLoading ? (
            <>
              Enviando...
              <Loader2 className="ml-2 h-4 w-4 animate-spin" />
            </>
          ) : (
            <>
              Calcular Frete <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>

      {/* Footer Desktop */}
      <div className="hidden lg:block p-6 pt-0">
        <div className="flex justify-between mt-6">
          <Button
            type="button"
            variant="link"
            className="underline"
            onClick={onBackClick}
            id="btn-cotacao-step-back-vehicle"
          >
            Voltar
          </Button>
          <Button
            type="button"
            id="btn-cotacao-step-final-desktop"
            disabled={isLoading}
            onClick={form.handleSubmit(onSubmit)}
          >
            {isLoading ? (
              <>
                Enviando...
                <Loader2 className="ml-2 h-4 w-4 animate-spin" />
              </>
            ) : (
              <>
                Calcular Frete <ArrowRight className="ml-2 " />
              </>
            )}
          </Button>
        </div>
      </div>

      <QuoteFeedbackModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        type={modalType}
        message={modalMessage}
      />
    </div>
  );
}
